import {
  RANGER_WEAPONS,
  getCooldownState,
  getDirectionAngle,
  getPatrolFacingDirection,
} from '../rangerLogic';
import { ENEMY_ARCHETYPES, getRangerPlayableVisual } from '../rangerVisuals';

export function createGround(scene, level) {
  const { width, height, groundPatches = [] } = level;

  scene.add.rectangle(width / 2, height / 2, width, height, 0x6e5635, 1);
  scene.add.rectangle(width / 2, height / 2, width - 28, height - 28, 0xd2bc7b, 1)
    .setStrokeStyle(4, 0xf3df9b, 0.76);
  scene.add.rectangle(width / 2, height / 2, width - 72, height - 52, 0xbc9d61, 1)
    .setStrokeStyle(2, 0x765937, 0.5);
  scene.add.rectangle(width / 2, height - 68, width - 74, 92, 0xa47c4f, 1)
    .setStrokeStyle(2, 0x60432b, 0.42);
  scene.add.rectangle(width / 2, 88, width - 74, 84, 0xdcc57b, 1)
    .setStrokeStyle(2, 0x8f6b38, 0.36);

  groundPatches.forEach((patch) => {
    scene.add.ellipse(
      patch.x,
      patch.y,
      patch.width,
      patch.height,
      patch.color ?? 0x8d9a58,
      patch.alpha
    );
  });

  for (let y = 142; y < height - 120; y += 72) {
    scene.add.line(width / 2, y, 36, 0, width - 36, 0, 0xf6e7a7, 0.22)
      .setLineWidth(4);
  }

  scene.add.text(42, height - 108, 'LZ', {
    fontFamily: 'monospace',
    fontSize: '18px',
    color: '#f8e7aa',
    fontStyle: 'bold',
  }).setDepth(2);
}

export function createTerrain(scene, terrain) {
  if (terrain.type === 'road') {
    scene.add.rectangle(terrain.x, terrain.y, terrain.width, terrain.height, 0xd7ad5f, 1)
      .setStrokeStyle(3, 0x7c5c2e, 0.52);

    for (let y = terrain.y - terrain.height / 2 + 28; y < terrain.y + terrain.height / 2; y += 42) {
      scene.add.rectangle(terrain.x, y, terrain.width - 14, 4, 0xf9dc8b, 0.42);
    }
    return;
  }

  if (terrain.type === 'trench') {
    scene.add.rectangle(terrain.x, terrain.y, terrain.width, terrain.height, 0x68442c, 1)
      .setStrokeStyle(3, 0xe0bc72, 0.66);
    scene.add.rectangle(terrain.x, terrain.y, terrain.width - 18, terrain.height - 20, 0x2f2118, 0.66);

    for (let x = terrain.x - terrain.width / 2 + 14; x < terrain.x + terrain.width / 2; x += 18) {
      scene.add.rectangle(x, terrain.y - terrain.height / 2 + 6, 10, 7, 0xe0c982, 0.72);
      scene.add.rectangle(x, terrain.y + terrain.height / 2 - 6, 10, 7, 0xe0c982, 0.72);
    }
    return;
  }

  scene.add.ellipse(terrain.x, terrain.y, terrain.width, terrain.height, 0x7c5f35, 0.86)
    .setStrokeStyle(3, 0x4e3821, 0.52);
  scene.add.ellipse(terrain.x + 10, terrain.y - 8, terrain.width * 0.62, terrain.height * 0.48, 0xd1a35c, 0.24);
}

export function createObstacle(scene, obstacle) {
  const obstacleSprite = scene.add.rectangle(
    obstacle.x,
    obstacle.y,
    obstacle.width,
    obstacle.height,
    0x7c754e,
    0
  );
  scene.physics.add.existing(obstacleSprite, true);

  const bagCount = Math.max(3, Math.floor(obstacle.width / 24));
  for (let index = 0; index < bagCount; index += 1) {
    const bagX = obstacle.x - obstacle.width / 2 + 12 + index * 24;
    scene.add.ellipse(bagX, obstacle.y, 27, obstacle.height + 8, 0xb7a16a, 1)
      .setStrokeStyle(2, 0x685638, 0.72)
      .setDepth(2);
  }
  scene.add.rectangle(obstacle.x, obstacle.y, obstacle.width + 8, 6, 0xf0d68f, 0.2)
    .setDepth(3);

  return obstacleSprite;
}

export function createDecoration(scene, decoration) {
  if (decoration.type === 'crate') {
    scene.add.rectangle(decoration.x, decoration.y, 30, 30, 0xb97942, 1)
      .setStrokeStyle(3, 0x4d3322, 0.78)
      .setDepth(2);
    scene.add.line(decoration.x, decoration.y, -10, -10, 10, 10, 0x4d3322, 0.42)
      .setLineWidth(2);
    scene.add.line(decoration.x, decoration.y, -10, 10, 10, -10, 0x4d3322, 0.42)
      .setLineWidth(2);
    return;
  }

  if (decoration.type === 'sandbag') {
    [-14, 0, 14].forEach((offset) => {
      scene.add.ellipse(decoration.x + offset, decoration.y, 24, 14, 0xcab676, 1)
        .setStrokeStyle(2, 0x6b5b39, 0.68)
        .setDepth(2);
    });
    return;
  }

  scene.add.polygon(decoration.x, decoration.y, [
    -13, 6,
    -7, -10,
    8, -12,
    15, 2,
    4, 12,
  ], 0x8c957f, 1)
    .setStrokeStyle(2, 0x4f584a, 0.72)
    .setDepth(2);
}

export function createFlag(scene, flag) {
  scene.add.ellipse(flag.x, flag.y + 8, 58, 42, 0xf7d979, 0.22)
    .setStrokeStyle(2, 0x9a7434, 0.45)
    .setDepth(1);
  scene.add.rectangle(flag.x - 14, flag.y + 12, 4, 56, 0xf7ecc4, 1)
    .setStrokeStyle(1, 0x65654c, 0.8)
    .setDepth(2);
  scene.add.triangle(
    flag.x + 2,
    flag.y - 2,
    -16,
    -16,
    20,
    -8,
    -16,
    0,
    0xf1cf57,
    1
  ).setStrokeStyle(2, 0x745c1b, 0.72).setDepth(2);
  scene.add.circle(flag.x - 14, flag.y - 18, 5, 0xf6e39a, 1)
    .setStrokeStyle(2, 0x705324, 0.72)
    .setDepth(2);

  const flagZone = scene.add.rectangle(
    flag.x,
    flag.y,
    flag.width,
    flag.height,
    0xf1cf57,
    0.08
  );
  scene.physics.add.existing(flagZone, true);

  return flagZone;
}

export function createGrenadeCooldownHud(scene) {
  scene.grenadeCooldownText = scene.add.text(scene.level.width - 42, scene.level.height - 34, 'G Ready', {
    fontFamily: 'monospace',
    fontSize: '11px',
    color: '#fff2ad',
    fontStyle: 'bold',
    stroke: '#2d2315',
    strokeThickness: 3,
  }).setOrigin(0.5).setDepth(18);
}

export function updateGrenadeCooldownHud(scene) {
  if (!scene.grenadeCooldownText) {
    return;
  }

  const cooldown = getCooldownState({
    currentTimeMs: scene.time.now,
    lastFireTimeMs: scene.lastWeaponFireTimes.grenade,
    cooldownMs: RANGER_WEAPONS.grenade.cooldownMs,
  });

  scene.grenadeCooldownText
    .setText(cooldown.isReady ? 'G Ready' : `G ${(cooldown.remainingMs / 1000).toFixed(1)}`)
    .setFill(cooldown.isReady ? '#fff2ad' : '#d9c48f')
    .setAlpha(cooldown.isReady ? 1 : 0.72 + cooldown.progress * 0.2);
}

export function createAlly(scene, ally) {
  const marker = scene.add.ellipse(ally.x, ally.y + 15, 34, 12, 0x1e2d25, 0.28).setDepth(2);
  const legs = scene.add.rectangle(ally.x, ally.y + 16, 20, 10, 0x26382f, 1)
    .setStrokeStyle(1, 0x152019, 0.72)
    .setDepth(4);
  const body = scene.add.rectangle(ally.x, ally.y + 2, 24, 28, 0x6f8c7e, 1)
    .setStrokeStyle(2, 0x25372f, 0.82)
    .setDepth(4);
  const vest = scene.add.rectangle(ally.x, ally.y + 5, 14, 19, 0x31493e, 0.72)
    .setDepth(5);
  const radioPack = scene.add.rectangle(ally.x - 14, ally.y + 5, 8, 20, 0x26382f, 1)
    .setStrokeStyle(1, 0x152019, 0.72)
    .setDepth(5);
  const antenna = scene.add.line(ally.x - 17, ally.y - 7, 0, 0, -4, -18, 0xd7e0c1, 0.75)
    .setLineWidth(2)
    .setDepth(6);
  const leftArm = scene.add.rectangle(ally.x - 14, ally.y + 3, 7, 22, 0x4f6c5d, 1)
    .setDepth(5);
  const rightArm = scene.add.rectangle(ally.x + 14, ally.y + 3, 7, 22, 0x4f6c5d, 1)
    .setDepth(5);
  const head = scene.add.ellipse(ally.x, ally.y - 14, 23, 17, 0x8aa899, 1)
    .setStrokeStyle(2, 0x25372f, 0.82)
    .setDepth(6);
  const helmetBand = scene.add.rectangle(ally.x, ally.y - 17, 20, 3, 0xcde0bd, 0.5)
    .setDepth(7);
  const face = scene.add.ellipse(ally.x + 2, ally.y - 9, 11, 6, 0xc79a68, 0.9)
    .setDepth(7);
  const zone = scene.add.rectangle(ally.x, ally.y, ally.width, ally.height, 0x9ed7bb, 0.08);
  scene.physics.add.existing(zone, true);
  scene.allyVisual = {
    marker,
    legs,
    body,
    vest,
    radioPack,
    antenna,
    leftArm,
    rightArm,
    head,
    helmetBand,
    face,
  };
  return zone;
}

export function createTarget(scene, target, index = 0) {
  const targetSprite = scene.add.rectangle(target.x, target.y, target.width, target.height, 0xa86432, 1)
    .setStrokeStyle(3, 0x4b2f1e, 0.82)
    .setDepth(4);
  scene.add.rectangle(target.x, target.y + 10, target.width - 10, 10, 0x5b3621, 0.84).setDepth(5);
  scene.add.rectangle(target.x, target.y - 8, target.width - 14, 12, 0xf0ca6a, 0.5).setDepth(5);
  scene.add.rectangle(target.x - 11, target.y - 1, 8, 13, 0x34291d, 0.82)
    .setStrokeStyle(1, 0xf0ca6a, 0.44)
    .setDepth(6);
  scene.add.circle(target.x + 8, target.y - 2, 5, 0xf0ca6a, 0.95)
    .setStrokeStyle(1, 0x4b2f1e, 0.72)
    .setDepth(6);
  scene.add.circle(target.x + 8, target.y - 2, 2, 0x402918, 1).setDepth(7);
  scene.add.line(target.x, target.y - 18, 0, 0, -9, -18, 0xf5e0a4, 0.72)
    .setLineWidth(2)
    .setDepth(6);
  scene.add.line(target.x, target.y - 18, 0, 0, 9, -18, 0xf5e0a4, 0.72)
    .setLineWidth(2)
    .setDepth(6);
  scene.add.text(target.x, target.y + 8, `${index + 1}`, {
    fontFamily: 'monospace',
    fontSize: '10px',
    color: '#fff2b5',
    fontStyle: 'bold',
  }).setOrigin(0.5).setDepth(7);
  scene.physics.add.existing(targetSprite, true);
  return targetSprite;
}

export function drawSightCone(scene, enemy) {
  const data = enemy.enemyData;
  const range = data.visionRange ?? 150;
  const angle = ((data.visionAngle ?? 58) * Math.PI / 180) / 2;
  const facingAngle = getDirectionAngle(data.facingDirection);
  const left = facingAngle - angle;
  const right = facingAngle + angle;
  const isAlerted = (data.alertedUntil ?? 0) > scene.time.now;
  const color = isAlerted ? 0xffad5f : 0xf3d985;
  const alpha = isAlerted ? 0.2 : 0.11;

  data.visionGraphic.clear();
  data.visionGraphic.fillStyle(color, alpha);
  data.visionGraphic.lineStyle(1, color, isAlerted ? 0.42 : 0.26);
  data.visionGraphic.beginPath();
  data.visionGraphic.moveTo(enemy.x, enemy.y);
  data.visionGraphic.lineTo(enemy.x + Math.cos(left) * range, enemy.y + Math.sin(left) * range);
  data.visionGraphic.lineTo(enemy.x + Math.cos(right) * range, enemy.y + Math.sin(right) * range);
  data.visionGraphic.closePath();
  data.visionGraphic.fillPath();
  data.visionGraphic.strokePath();
}

export function syncEnemyDetails(enemy) {
  const data = enemy.enemyData;
  const side = data.facingDirection === 'right' ? 1 : data.facingDirection === 'left' ? -1 : 0;
  const lookingUp = data.facingDirection === 'up';
  const isAlerted = data.awarenessState !== 'patrol';

  data.leftArm
    .setPosition(side ? -side * 7 : -14, 4)
    .setSize(7, side ? 18 : 22)
    .setAngle(side ? -16 : 0);
  data.rightArm
    .setPosition(side ? side * 8 : 14, 4)
    .setSize(7, side ? 18 : 22)
    .setAngle(side ? 16 : 0);
  data.visor
    .setPosition(side ? side * 7 : 0, lookingUp ? -16 : -10)
    .setSize(side ? 7 : 14, 3)
    .setAlpha(lookingUp ? 0.28 : 0.65);
  data.rifle
    .setPosition(side ? side * 16 : 12, lookingUp ? -3 : 5)
    .setAngle(side ? 90 : lookingUp ? -8 : 18)
    .setAlpha(data.defeated ? 0.35 : 1);
  data.rifleStock
    .setPosition(side ? side * 8 : 8, lookingUp ? 10 : 15)
    .setAngle(side ? 90 : 18)
    .setAlpha(data.defeated ? 0.28 : 0.9);
  data.rifleBarrel
    .setPosition(side ? side * 27 : 16, lookingUp ? -16 : -8)
    .setSize(side ? 18 : 3, side ? 3 : 20)
    .setAngle(side ? 0 : lookingUp ? -8 : 18)
    .setAlpha(data.defeated ? 0.28 : 1);
  data.alertMarker.setVisible(!data.defeated && isAlerted);
}

export function createEnemy(scene, enemy, index) {
  const fallbackTypes = ['basic', 'heavy', 'elite'];
  const archetype = ENEMY_ARCHETYPES[enemy.type] ?? ENEMY_ARCHETYPES[fallbackTypes[index % fallbackTypes.length]];
  const visionGraphic = scene.add.graphics().setDepth(3);
  const container = scene.add.container(enemy.x, enemy.y).setDepth(9);
  container.setScale(archetype.scale ?? 1);
  const shadow = scene.add.ellipse(0, 16, 32, 11, 0x1a160f, 0.3);
  const boots = scene.add.rectangle(0, 23, 24, 7, 0x18140e, 1);
  const legs = scene.add.rectangle(0, 17, 22, 11, 0x2f2a1e, 1)
    .setStrokeStyle(1, 0x18140e, 0.68);
  const pack = scene.add.rectangle(-12, 5, 8, 23, 0x3c3023, 1)
    .setStrokeStyle(1, 0x17120d, 0.68);
  const leftArm = scene.add.rectangle(-14, 4, 7, 22, 0x4b3b2d, 1);
  const rightArm = scene.add.rectangle(14, 4, 7, 22, 0x4b3b2d, 1);
  const body = scene.add.rectangle(0, 4, 24, 29, archetype.body, 1)
    .setStrokeStyle(2, 0x2a2018, 0.8);
  const chestStrap = scene.add.rectangle(-5, 4, 4, 25, 0x2a2018, 0.55)
    .setAngle(-12);
  const belt = scene.add.rectangle(0, 10, 23, 4, 0x2a2018, 0.9);
  const helmet = scene.add.ellipse(0, -13, 27, 18, archetype.helmet, 1)
    .setStrokeStyle(2, 0x3c2f20, 0.85);
  const helmetRim = scene.add.rectangle(0, -10, 29, 4, 0x3c2f20, 0.52);
  const face = scene.add.ellipse(2, -7, 11, 6, 0xb9885f, 0.92);
  const visor = scene.add.rectangle(0, -10, 14, 3, archetype.visor, 0.72);
  const rifle = scene.add.rectangle(15, 5, 5, 28, 0x201c16, 1)
    .setStrokeStyle(1, 0x0d0b08, 0.8);
  const rifleStock = scene.add.rectangle(8, 15, 8, 7, 0x6a4326, 1)
    .setStrokeStyle(1, 0x25170c, 0.72);
  const rifleBarrel = scene.add.rectangle(17, -8, 3, 20, 0x14130f, 1);
  const alertMarker = scene.add.triangle(0, -34, -6, 6, 6, 6, 0, -8, 0xffd06b, 1)
    .setStrokeStyle(1, 0x5f341a, 0.8)
    .setVisible(false);
  const rankMark = archetype.mark === 'chevron'
    ? scene.add.triangle(0, -33, -8, 5, 8, 5, 0, -7, archetype.accent, 1)
    : scene.add.rectangle(0, -33, archetype.mark === 'armor' ? 17 : 13, archetype.mark === 'armor' ? 8 : 4, archetype.accent, 1);
  rankMark.setStrokeStyle(1, 0x3c2f20, 0.78);
  const armorPlate = scene.add.rectangle(0, 0, 18, 12, 0x2a2018, archetype.mark === 'armor' ? 0.42 : 0)
    .setStrokeStyle(1, archetype.accent, archetype.mark === 'armor' ? 0.5 : 0);

  container.add([
    shadow,
    boots,
    legs,
    pack,
    leftArm,
    rightArm,
    body,
    armorPlate,
    chestStrap,
    belt,
    helmet,
    helmetRim,
    face,
    visor,
    rifleStock,
    rifle,
    rifleBarrel,
    alertMarker,
    rankMark,
  ]);
  scene.physics.add.existing(container);
  container.body.setSize(24, 32);
  container.body.setOffset(-12, -16);
  container.body.setAllowGravity(false);
  container.enemyData = {
    ...enemy,
    type: archetype.type,
    index,
    health: enemy.health ?? archetype.health,
    originX: enemy.x,
    originY: enemy.y,
    direction: 1,
    facingDirection: getPatrolFacingDirection({ ...enemy, direction: 1 }),
    pendingFacingDirection: null,
    waitUntilMs: 0,
    nextShotTime: 0,
    alertedUntil: 0,
    awarenessState: 'patrol',
    investigationMs: enemy.investigationMs ?? archetype.investigationMs,
    attackCooldownMs: enemy.attackCooldownMs ?? archetype.attackCooldownMs,
    shotSpeed: enemy.shotSpeed ?? archetype.shotSpeed,
    speed: (enemy.speed ?? 24) * archetype.speedMultiplier,
    visionGraphic,
    alertMarker,
    leftArm,
    rightArm,
    visor,
    rifle,
    rifleStock,
    rifleBarrel,
  };
  drawSightCone(scene, container);
  return container;
}

export function createPlayer(scene, playerSpawn, selectedPlayableIcon) {
  scene.playerShadow = scene.add.ellipse(playerSpawn.x, playerSpawn.y + 14, 34, 13, 0x1c1a12, 0.3)
    .setDepth(1);
  const palette = getRangerPlayableVisual(selectedPlayableIcon);
  const role = palette.role ?? 'ranger';
  const isMedic = role === 'medic';
  const isSpy = role === 'spy';
  scene.player = scene.add.rectangle(
    playerSpawn.x,
    playerSpawn.y,
    30,
    34,
    palette.torso,
    0
  );
  scene.player.setDepth(2);
  scene.physics.add.existing(scene.player);
  scene.player.body.setCollideWorldBounds(true);

  scene.playerVisual = {
    role,
    leftBoot: scene.add.rectangle(playerSpawn.x - 7, playerSpawn.y + 20, 9, 9, palette.helmetDark, 1)
      .setDepth(4),
    rightBoot: scene.add.rectangle(playerSpawn.x + 7, playerSpawn.y + 20, 9, 9, palette.helmetDark, 1)
      .setDepth(4),
    legs: scene.add.rectangle(playerSpawn.x, playerSpawn.y + 15, 23, 13, palette.helmetDark, 0.95)
      .setStrokeStyle(1, 0x171d13, 0.66)
      .setDepth(4),
    leftArm: scene.add.rectangle(playerSpawn.x - 14, playerSpawn.y + 2, 8, 24, palette.helmetDark, 1)
      .setDepth(4),
    rightArm: scene.add.rectangle(playerSpawn.x + 14, playerSpawn.y + 2, 8, 24, palette.helmetDark, 1)
      .setDepth(4),
    torso: scene.add.rectangle(playerSpawn.x, playerSpawn.y + 3, 24, 28, palette.torso, 1)
      .setStrokeStyle(2, palette.outline ?? 0x26301f, 0.85)
      .setDepth(4),
    belt: scene.add.rectangle(playerSpawn.x, playerSpawn.y + 11, 25, 4, palette.helmetDark, 0.9)
      .setDepth(6),
    vest: scene.add.rectangle(playerSpawn.x, playerSpawn.y + 6, 14, 18, palette.vest, 0.88)
      .setDepth(5),
    leftStrap: scene.add.rectangle(playerSpawn.x - 5, playerSpawn.y + 4, 3, 21, palette.helmetDark, 0.65)
      .setDepth(5),
    rightStrap: scene.add.rectangle(playerSpawn.x + 5, playerSpawn.y + 4, 3, 21, palette.helmetDark, 0.65)
      .setDepth(5),
    pack: scene.add.rectangle(playerSpawn.x, playerSpawn.y + 14, 20, 10, palette.pack ?? palette.helmetDark, 0.84)
      .setStrokeStyle(1, palette.accent, 0.26)
      .setDepth(5),
    canteen: scene.add.circle(playerSpawn.x - 13, playerSpawn.y + 12, 4, palette.accent, 0.72)
      .setStrokeStyle(1, palette.helmetDark, 0.6)
      .setDepth(6),
    helmet: scene.add.ellipse(playerSpawn.x, playerSpawn.y - 13, 29, 22, palette.helmet, 1)
      .setStrokeStyle(2, palette.helmetDark, 0.9)
      .setDepth(6),
    helmetRim: scene.add.rectangle(playerSpawn.x, playerSpawn.y - 10, 31, 4, palette.helmetDark, 0.62)
      .setDepth(7),
    helmetStripe: scene.add.rectangle(playerSpawn.x, playerSpawn.y - 16, 18, 3, palette.accent, 0.48)
      .setDepth(7),
    scarf: scene.add.rectangle(playerSpawn.x, playerSpawn.y - 2, 13, 4, palette.accent, 0.78)
      .setDepth(7),
    face: scene.add.ellipse(playerSpawn.x, playerSpawn.y - 7, 13, 7, palette.skin ?? 0xc59665, 0.95)
      .setDepth(7),
    visor: scene.add.rectangle(playerSpawn.x, playerSpawn.y - 8, 13, 3, 0x151a14, 0.7)
      .setDepth(8),
    medicCrossHorizontal: scene.add.rectangle(playerSpawn.x + 8, playerSpawn.y - 15, 13, 4, 0xd94842, isMedic ? 1 : 0)
      .setDepth(8),
    medicCrossVertical: scene.add.rectangle(playerSpawn.x + 8, playerSpawn.y - 15, 4, 13, 0xd94842, isMedic ? 1 : 0)
      .setDepth(8),
    classPatch: scene.add.rectangle(playerSpawn.x + 9, playerSpawn.y + 2, 7, 12, palette.classMark ?? palette.accent, isSpy ? 0.9 : 0.72)
      .setStrokeStyle(1, palette.outline ?? palette.helmetDark, 0.42)
      .setDepth(7),
    gogglesLeft: scene.add.circle(playerSpawn.x - 4, playerSpawn.y - 8, 3, palette.classMark ?? palette.accent, isSpy ? 0.9 : 0)
      .setStrokeStyle(1, 0x111a17, isSpy ? 0.75 : 0)
      .setDepth(9),
    gogglesRight: scene.add.circle(playerSpawn.x + 5, playerSpawn.y - 8, 3, palette.classMark ?? palette.accent, isSpy ? 0.9 : 0)
      .setStrokeStyle(1, 0x111a17, isSpy ? 0.75 : 0)
      .setDepth(9),
    gogglesBridge: scene.add.rectangle(playerSpawn.x + 1, playerSpawn.y - 8, 7, 1, 0x111a17, isSpy ? 0.7 : 0)
      .setDepth(9),
    rifleSling: scene.add.rectangle(playerSpawn.x + 6, playerSpawn.y + 4, 3, 30, palette.accent, 0.3)
      .setAngle(-26)
      .setDepth(6),
    rifle: scene.add.rectangle(playerSpawn.x + 17, playerSpawn.y + 5, 5, 30, palette.rifle ?? 0x232921, 1)
      .setStrokeStyle(1, 0x0d100c, 0.8)
      .setDepth(7),
    rifleStock: scene.add.rectangle(playerSpawn.x + 13, playerSpawn.y + 14, 8, 7, palette.rifleStock ?? 0x5b3a20, 1)
      .setStrokeStyle(1, 0x22170d, 0.72)
      .setDepth(7),
    rifleBarrel: scene.add.rectangle(playerSpawn.x + 21, playerSpawn.y - 9, 3, 20, 0x151711, 1)
      .setDepth(8),
    muzzle: scene.add.rectangle(playerSpawn.x + 21, playerSpawn.y - 20, 6, 3, palette.accent, 0.92)
      .setDepth(9),
  };
}

export function syncPlayerDetails(scene, isMoving = false) {
  scene.playerShadow.setPosition(scene.player.x, scene.player.y + 14);
  scene.playerShadow.setScale(isMoving ? 1.08 : 1, isMoving ? 0.92 : 1);

  const parts = scene.playerVisual;
  const bobOffset = isMoving ? Math.sin(scene.time.now / 75) * 1.8 : 0;
  const x = scene.player.x;
  const y = scene.player.y + bobOffset;
  const facing = scene.playerFacingDirection;
  const side = facing === 'right' ? 1 : facing === 'left' ? -1 : 0;
  const lookingUp = facing === 'up';
  const lookingDown = facing === 'down';
  const rifleX = x + (side || 1) * 17;

  parts.legs.setPosition(x, y + 15).setSize(side ? 18 : 23, 13);
  parts.leftBoot.setPosition(x - (side ? 5 : 7), y + 21);
  parts.rightBoot.setPosition(x + (side ? 5 : 7), y + 21);
  parts.torso.setPosition(x, y + 3).setSize(side ? 20 : 24, 28);
  parts.belt.setPosition(x - side * 1, y + 11).setSize(side ? 20 : 25, 4);
  parts.vest.setPosition(x - side * 2, y + 6).setSize(side ? 10 : 14, 18);
  parts.leftStrap
    .setPosition(x - (side ? 3 : 5), y + 4)
    .setSize(3, side ? 17 : 21)
    .setAlpha(lookingUp ? 0.35 : 0.65);
  parts.rightStrap
    .setPosition(x + (side ? 3 : 5), y + 4)
    .setSize(3, side ? 17 : 21)
    .setAlpha(lookingUp ? 0.35 : 0.65);
  parts.helmet.setPosition(x + side * 4, y - 13).setSize(side ? 24 : 29, 22);
  parts.helmetRim.setPosition(x + side * 5, y - 10).setSize(side ? 18 : 31, 4);
  parts.helmetStripe.setPosition(x + side * 4, y - 16).setSize(side ? 10 : 18, 3);
  parts.scarf
    .setPosition(x + side * 3, y - 2)
    .setSize(side ? 9 : 13, 4)
    .setAlpha(lookingUp ? 0.28 : 0.78);
  parts.face
    .setPosition(x + side * 8, y + (lookingUp ? -16 : -7))
    .setSize(side ? 7 : 13, lookingUp ? 4 : 7)
    .setAlpha(lookingUp ? 0.18 : 0.95);
  parts.visor
    .setPosition(x + side * 8, y + (lookingUp ? -17 : -8))
    .setSize(side ? 6 : 13, lookingUp ? 3 : 3)
    .setAlpha(lookingUp ? 0.22 : 0.76);
  parts.medicCrossHorizontal
    .setPosition(x + side * 5, y - 15)
    .setSize(side ? 8 : 13, 4)
    .setAlpha(parts.role === 'medic' && !lookingUp ? 1 : 0);
  parts.medicCrossVertical
    .setPosition(x + side * 5, y - 15)
    .setSize(4, side ? 8 : 13)
    .setAlpha(parts.role === 'medic' && !lookingUp ? 1 : 0);
  parts.classPatch
    .setPosition(x + (side ? side * 7 : 9), y + 2)
    .setSize(side ? 5 : 7, side ? 10 : 12)
    .setAlpha(lookingUp ? 0.3 : 0.78);
  parts.gogglesLeft
    .setPosition(x + side * 6 - (side ? 0 : 4), y - 8)
    .setAlpha(parts.role === 'spy' && !lookingUp ? 0.9 : 0);
  parts.gogglesRight
    .setPosition(x + side * 6 + (side ? 0 : 5), y - 8)
    .setAlpha(parts.role === 'spy' && !lookingUp ? 0.9 : 0);
  parts.gogglesBridge
    .setPosition(x + side * 6 + 1, y - 8)
    .setSize(side ? 1 : 7, side ? 7 : 1)
    .setAlpha(parts.role === 'spy' && !lookingUp ? 0.7 : 0);
  parts.pack
    .setPosition(x - side * 7, y + (lookingUp ? -2 : 14))
    .setSize(lookingUp ? 22 : side ? 9 : 20, lookingUp ? 13 : 10)
    .setAlpha(lookingUp ? 0.95 : 0.74);
  parts.canteen
    .setPosition(x - (side ? side * 10 : 13), y + 12)
    .setAlpha(lookingUp ? 0.26 : 0.72);
  parts.leftArm
    .setPosition(x - (side ? 7 : 14), y + 3)
    .setSize(8, side ? 18 : 24)
    .setAngle(side ? -18 : 0);
  parts.rightArm
    .setPosition(x + (side ? 7 : 14), y + 3)
    .setSize(8, side ? 18 : 24)
    .setAngle(side ? 18 : 0);
  parts.rifleSling
    .setPosition(x + (side ? side * 3 : 6), y + 4)
    .setSize(3, side ? 24 : 30)
    .setAngle(side ? side * 18 : -26)
    .setAlpha(lookingUp ? 0.22 : 0.3);
  parts.rifle
    .setPosition(rifleX, y + (side ? 4 : lookingDown ? 7 : 1))
    .setAngle(side ? 90 : lookingUp ? -4 : 18)
    .setAlpha(lookingUp ? 0.35 : 1);
  parts.rifleStock
    .setPosition(x + (side || 1) * (side ? 10 : 13), y + (side ? 8 : 15))
    .setSize(side ? 9 : 8, side ? 6 : 7)
    .setAngle(side ? 90 : 18)
    .setAlpha(lookingUp ? 0.3 : 1);
  parts.rifleBarrel
    .setPosition(x + (side || 1) * (side ? 27 : 21), y + (side ? 3 : lookingUp ? -14 : -8))
    .setSize(side ? 20 : 3, side ? 3 : 20)
    .setAngle(side ? 0 : lookingUp ? -4 : 18)
    .setAlpha(lookingUp ? 0.4 : 1);
  parts.muzzle
    .setPosition(x + (side || 1) * (side ? 38 : 22), y + (side ? 3 : lookingUp ? -25 : -19))
    .setSize(side ? 5 : 6, side ? 6 : 3)
    .setAlpha(lookingUp ? 0.35 : 0.92);
}
